// components/StatCardGrid.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";

interface StatItem {
  title: string;
  value: number | string;
}
interface StatCardGridProps {
  items: StatItem[];
  formatValue?: (value: number | string) => string;
  currencySymbol?: boolean;
}

const StatCardGrid: React.FC<StatCardGridProps> = ({
  items,
  formatValue = (val) => String(val),
  currencySymbol = false,
}) => {
  // match Bootstrap xs breakpoint
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 576
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // refs for tooltip anchors
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // tooltip state
  const [tip, setTip] = useState<
    | {
        index: number;
        x: number;
        y: number;
        h: number;
        place: "above" | "below";
      }
    | null
  >(null);

  useEffect(() => {
    if (!tip) return;
    const t = setTimeout(() => setTip(null), 2500);
    return () => clearTimeout(t);
  }, [tip]);

  useEffect(() => {
    if (!tip) return;
    const close = () => setTip(null);
    document.addEventListener("scroll", close, true);
    document.addEventListener("touchmove", close as any, { passive: true } as any);
    return () => {
      document.removeEventListener("scroll", close, true);
      document.removeEventListener("touchmove", close as any);
    };
  }, [tip]);

  const handleTap = (index: number) => {
    if (!isMobile) return;
    const el = itemRefs.current[index];
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const x = rect.left + rect.width / 2 + window.scrollX;
    const y = rect.top + window.scrollY;
    const place: "above" | "below" = rect.top < 120 ? "below" : "above";
    setTip({ index, x, y, h: rect.height, place });
  };

  const bubbleBase: React.CSSProperties = useMemo(
    () => ({
      position: "fixed",
      zIndex: 1056,
      maxWidth: "86vw",
      background: "#111",
      color: "#fff",
      padding: "10px 12px",
      borderRadius: 10,
      boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
      fontSize: 14,
      lineHeight: 1.3,
      transform: "translate(-50%, -100%)",
      WebkitUserSelect: "none",
      userSelect: "none",
    }),
    []
  );

  // --- chunk items into rows of 3 on mobile ---
  const chunk = <T,>(arr: T[], size: number) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size)
    );

  if (isMobile) {
    const rows = chunk(items, 3); // exactly 3 per row
    return (
      <>
        {rows.map((row, rIdx) => (
          <div className="row g-2 pb-2 custom-att-boxes" key={`row-${rIdx}`}>
            {row.map((item, cIdx) => {
              const globalIndex = rIdx * 3 + cIdx;
              return (
                <div key={globalIndex} className="col-4" style={{ minWidth: 0 }}>
                  <div
                    ref={(el) => (itemRefs.current[globalIndex] = el)}
                    className="card flex-fill att-card h-100"
                    onClick={() => handleTap(globalIndex)}
                    role="button"
                    aria-label="Show details"
                    style={{ width: "100%", minWidth: 0 }}
                  >
                    <div className="card-body d-flex align-items-center justify-content-between box-padding">
                      <div className="w-100">
                        <p className="fs-16 mb-1 att-boxes-title text-truncate" title={item.title}>
                          {item.title}
                          {currencySymbol && " [₹]"}
                        </p>
                        <h3
                          className="mb-0 att-boxes-value text-truncate"
                          title={String(item.value)}
                        >
                          {formatValue(item.value)}
                        </h3>
                      </div>
                    </div>
                  </div>

                  {/* anchored tooltip */}
                  {tip && tip.index === globalIndex && (
                    <>
                      <div
                        onClick={() => setTip(null)}
                        style={{ position: "fixed", inset: 0, zIndex: 1055, background: "transparent" }}
                      />
                      <div
                        role="tooltip"
                        style={{
                          ...bubbleBase,
                          left: tip.x,
                          top: tip.place === "above" ? tip.y : tip.y + tip.h,
                          transform:
                            tip.place === "above"
                              ? "translate(-50%, calc(-100% - 10px))"
                              : "translate(-50%, 10px)",
                        }}
                      >
                        <div style={{ opacity: 0.85, fontSize: 12, marginBottom: 4 }}>
                          {currencySymbol ? `${item.title} [₹]` : item.title}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>
                          {formatValue(item.value)}
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 0,
                            height: 0,
                            borderLeft: "8px solid transparent",
                            borderRight: "8px solid transparent",
                            ...(tip.place === "above"
                              ? { bottom: -8, borderTop: "8px solid #111" }
                              : { top: -8, borderBottom: "8px solid #111" }),
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </>
    );
  }

  // --- desktop / tablet: normal Bootstrap grid ---
  return (
    <div className="row pb-2 custom-att-boxes row-cols-4 row-cols-lg-5 g-2">
      {items.map((item, index) => (
        <div key={index} className="col" style={{ minWidth: 0 }}>
          <div
            ref={(el) => (itemRefs.current[index] = el)}
            className="card flex-fill att-card h-100"
            onClick={() => handleTap(index)}
            aria-label={undefined}
            style={{ width: "100%", minWidth: 0 }}
          >
            <div className="card-body d-flex align-items-center justify-content-between box-padding">
              <div className="w-100">
                <p className="fs-16 mb-1 att-boxes-title text-truncate" title={item.title}>
                  {item.title}
                  {currencySymbol && " [₹]"}
                </p>
                <h3 className="mb-0 att-boxes-value text-truncate" title={String(item.value)}>
                  {formatValue(item.value)}
                </h3>
              </div>
            </div>
          </div>

          {/** tooltip logic for desktop stays disabled (tap-only) */}
        </div>
      ))}
    </div>
  );
};

export default StatCardGrid;
